import React from 'react';

const trustpilotReviews = [
  {
    name: 'Susanne',
    rating: 5,
    title: 'Flot arbejde',
    date: '13. august 2026',
    quote: 'Behagelige medarbejdere, der tilpassede arbejdet til vejr og omgivelser, og ryddede op efter sig. Meget tilfreds.'
  },
  {
    name: 'Michael',
    rating: 5,
    title: 'Syddanskmalerfirma har lige lavet et meget folt arbejde.',
    date: '3. juni 2026',
    quote: 'Det hele står kniv skarpt og meget flot malet. Er ovenud meget tilfreds over deres arbejde. Meget professionelle.'
  },
  {
    name: 'Søren',
    rating: 5,
    title: 'Vores bedste anbefalinger',
    date: '1. september 2021',
    quote: 'Vi vil ikke bekymre os ved at medgive Syddansk malerfirma og Mette Frank vores bedste anbefalinger.'
  }
];

export default function Testimonials() {
  const containerRef = React.useRef(null);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(true);
  const [activeIndex, setActiveIndex] = React.useState(0);

  const checkScroll = React.useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 10);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    
    // Beregn aktiv prik
    const itemWidth = el.firstElementChild?.clientWidth || 1;
    const gap = 16;
    const newIndex = Math.round(scrollLeft / (itemWidth + gap));
    setActiveIndex(Math.min(Math.max(newIndex, 0), trustpilotReviews.length - 1));
  }, []);

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener('scroll', checkScroll, { passive: true });
    window.addEventListener('resize', checkScroll);
    return () => {
      el.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, [checkScroll]);

  const scrollTo = (direction) => {
    const el = containerRef.current;
    if (!el) return;
    const scrollAmount = (el.firstElementChild?.clientWidth || 300) + 16;
    el.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    });
  };

  const scrollToIndex = (index) => {
    const el = containerRef.current;
    if (!el || !el.children[index]) return;
    const targetEl = el.children[index];
    targetEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  };

  return (
    <div className="max-w-[1600px] mx-auto px-4 sm:px-8 lg:px-12 w-full">
      {/* Header */}
      <div className="mb-8 sm:mb-12 text-center max-w-2xl mx-auto flex flex-col items-center">
        <span className="text-[#ee6425] font-extrabold tracking-wider uppercase text-xs sm:text-sm block mb-1">
          Det siger vores kunder
        </span>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-[#0c1e36] tracking-tight leading-[1.15] mb-2.5 sm:mb-3">
          Kundeoplevelser, vi er stolte af
        </h2>
        <div className="w-12 h-1 bg-[#ee6425] rounded-full mx-auto mb-3 sm:mb-4"></div>
        <p className="text-stone-600 text-sm sm:text-base leading-relaxed">
          Vi er stolte af den feedback, vi får fra vores kunder. Her kan du læse om deres oplevelser med Syddansk Malerfirma.
        </p>
      </div>

      {/* Anmeldelseskort: Swipebar karrusel på mobil/tablet, side om side på desktop */}
      <div className="relative max-w-6xl mx-auto">
        {/* Venstre/Højre piletaster til mobil/tablet & desktop */}
        {canScrollLeft && (
          <button
            type="button"
            onClick={() => scrollTo('left')}
            aria-label="Forrige anmeldelse"
            className="lg:hidden absolute -left-2 sm:-left-4 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white shadow-lg border border-slate-200 text-[#0c1e36] flex items-center justify-center hover:bg-slate-50 active:scale-95 transition-all"
          >
            <i className="fa-solid fa-chevron-left text-xs" />
          </button>
        )}
        {canScrollRight && (
          <button
            type="button"
            onClick={() => scrollTo('right')}
            aria-label="Næste anmeldelse"
            className="lg:hidden absolute -right-2 sm:-right-4 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white shadow-lg border border-slate-200 text-[#0c1e36] flex items-center justify-center hover:bg-slate-50 active:scale-95 transition-all"
          >
            <i className="fa-solid fa-chevron-right text-xs" />
          </button>
        )}

        <div
          ref={containerRef}
          className="flex lg:grid lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 overflow-x-auto lg:overflow-visible snap-x snap-mandatory touch-pan-x pb-4 pt-1 px-4 sm:px-0 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden overscroll-x-contain"
        >
          {trustpilotReviews.map((review, idx) => (
            <div
              key={idx}
              className="w-[85vw] max-w-[340px] sm:w-[360px] lg:w-auto shrink-0 lg:shrink snap-center bg-white rounded-2xl p-6 sm:p-7 flex flex-col justify-between shadow-[0_4px_24px_rgba(0,0,0,0.06)] border border-slate-100 transition-transform duration-200 hover:-translate-y-1 text-left select-none"
            >
              <div>
                {/* Trustpilot 5 stjerner */}
                <div className="flex items-center gap-1 mb-4" aria-label="5 ud af 5 stjerner">
                  {[...Array(review.rating)].map((_, i) => (
                    <div
                      key={i}
                      className="w-5 h-5 bg-[#00B67A] rounded-[2px] flex items-center justify-center text-white shadow-2xs"
                    >
                      <svg className="w-3 h-3 fill-current" viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                      </svg>
                    </div>
                  ))}
                </div>

                {/* Anmeldelsens titel */}
                <h3 className="text-base sm:text-lg font-bold text-[#0c1e36] tracking-tight leading-snug mb-2.5 break-words">
                  {review.title}
                </h3>

                {/* Anmeldelsesuddrag */}
                <p className="text-xs sm:text-sm text-[#4A5568] leading-relaxed">
                  "{review.quote}"
                </p>
              </div>

              {/* Forfatter & Trustpilot info */}
              <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between gap-2">
                <div>
                  <p className="font-bold text-[#0c1e36] text-xs sm:text-sm leading-tight">
                    {review.name}
                  </p>
                  <p className="text-[11px] sm:text-xs text-stone-500 font-normal mt-0.5">
                    {review.date}
                  </p>
                </div>

                {/* Trustpilot-angivelse */}
                <div className="flex items-center gap-1 text-[11px] sm:text-xs font-semibold text-[#0c1e36]">
                  <span className="text-[#00B67A] text-sm leading-none">★</span>
                  <span className="font-bold">Trustpilot</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Prik-indikatorer + Swipe hint for mobil */}
        <div className="flex lg:hidden flex-col items-center gap-2 mt-2">
          <div className="flex items-center gap-1.5" aria-hidden="true">
            {trustpilotReviews.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => scrollToIndex(i)}
                className={`transition-all duration-300 rounded-full ${
                  activeIndex === i ? 'w-6 h-2 bg-[#ee6425]' : 'w-2 h-2 bg-slate-300'
                }`}
                aria-label={`Gå til anmeldelse ${i + 1}`}
              />
            ))}
          </div>
          <div className="flex items-center justify-center gap-1.5 text-stone-400 text-xs">
            <i className="fa-solid fa-arrow-left-long text-[10px] animate-pulse" />
            <span>Swipe for at se flere</span>
            <i className="fa-solid fa-arrow-right-long text-[10px] animate-pulse" />
          </div>
        </div>
      </div>

      {/* Trustpilot CTA knap */}
      <div className="mt-8 sm:mt-12 flex justify-center px-4">
        <a
          href="https://dk.trustpilot.com/review/www.syddanskmalerfirma.dk"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 rounded-[14px] bg-[#ee6425] hover:bg-[#d95318] px-5 sm:px-6 py-3 text-xs sm:text-sm font-bold text-white shadow-md shadow-orange-500/20 transition-all active:scale-[0.98] uppercase tracking-wider text-center"
        >
          <span>Se Trustpilot anmeldelser</span>
          <i className="fa-solid fa-arrow-up-right-from-square text-xs" aria-hidden="true" />
        </a>
      </div>
    </div>
  );
}
